import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useSelector } from 'react-redux'
import numeral from 'numeral'
import moment from 'moment'

import Table from '../../../Invoices/Table'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'
import getUniqueArrayByKey from '../../../../helpers/getUniqueArrayByKey'

const columnHelper = createColumnHelper()

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const getDuration = (minutes) => {
  const duartion = moment.duration(minutes, 'minutes')
  const hours = Math.floor(duartion.asHours())
  const mins = duartion.minutes()
  return `${hours}h ${mins}m`
}

const TopBorrowedMaterials = ({transactions}) => {
  const { t } = useTranslation()
      
  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  const data = useMemo(() => {
    const invoices = transactions.filter(tr => tr.TransactionType === 'INVOICE')
    const productMap = invoices.reduce((acc, transaction) => {
      const productName = transaction['Product.Name'] ? transaction['Product.Name'].replace('ž:', '') : 'Uncategorized'
      return {
        ...acc,
        [productName]: acc[productName] ? [...acc[productName], transaction] : [transaction],
      }
    }, {})

    return Object.entries(productMap).map(([product, transactions]) => {
      const {amount, quantity} = transactions.reduce((acc, transaction) => {
        return {
          ...acc,
          amount: acc.amount + (transaction['Sum.Incoming'] || 0),
          quantity: acc.quantity + (transaction['Amount.In'] || 0),
        }
      }, {
        amount: 0,
        quantity: 0,
        unit: '',
      })
      
      return {
        name: product,
        category: transactions[0]['Product.TopParent'] || 'Uncategorized',
        supplier: transactions[0]['Counteragent.Name'] || 'Uncategorized',
        warehouse: transactions[0]['Account.Name'] || 'Uncategorized',
        quantity: `${numeral(quantity).format('0.[00]')} ${transactions[0]['Product.MeasureUnit'] || ''}`,
        price: amount > 0 ? amount / quantity : 0,
        amount: amount,
      }
    }).sort((a, b) => b.amount - a.amount).slice(0, 10)
  }, [transactions])

  const columns = useMemo(() => ([
    columnHelper.accessor('name', {
      id: 'name',
      header: t('Product'),
      enableSorting: false,
      cell: ({ getValue, row }) => {
        return `${getValue()}`
      }
    }),
    columnHelper.accessor('category', {
      id: 'category',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Category'),
    }),
    columnHelper.accessor('supplier', {
      id: 'supplier',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Supplier'),
    }),
    columnHelper.accessor('warehouse', {
      id: 'warehouse',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Warehouse'),
    }),
    columnHelper.accessor('quantity', {
      id: 'quantity',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Quantity'),
    }),
    columnHelper.accessor('price', {
      id: 'price',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Price'),
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Amount'),
    }),
  ]), [t, currency.symbol])

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('TOP borrowed raw materials')}</div>
      </div>
      <Table
        table={table}
        columns={columns}
        wrapperStyle={{flex: 1}}
        headerClassName={styles.tableHeader} />
    </div>
  )
}

export default TopBorrowedMaterials
